import store from '@state/store'
import { getCoreAxiosInstance } from '@utils/axios'
import { tryPerformSsoRedirect } from '@state/authFlow'
import Timeout from '@views/_timeout'
import NotFound from '@views/_404'

// Views load lazily in section-sized chunks: a shared webpackChunkName
// groups a section's views into one file, so the first visit to a section
// pays one fetch and its siblings are free. `webpackPrefetch` marks a chunk
// for idle-time warm-up after the app shell loads; the hint covers the
// whole chunk, so it appears on only one import per group. Heavy
// single-view chunks (calendar, print-editor, diet-editor) stay separate
// on purpose. Chunk-load failures (e.g. stale hashes right after a deploy)
// are handled centrally in ./index.js.
export default [
  {
    path: '/',
    name: 'home',
    component: () => import(/* webpackChunkName: "home", webpackPrefetch: true */ '@views/home'),
    meta: {
      authRequired: true,
      hideBreadcrumbs: true
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "auth" */ '@views/auth/login'),
    meta: {
      layout: 'auth',
      async beforeResolve(routeTo, routeFrom, next) {
        if (store.getters['auth/loggedIn']) {
          // Already authed. If they got here via the vres SSO reverse flow
          // (`?sso_return=1`), bridge them through instead of dumping them
          // on /home — they didn't come here to look at their dashboard.
          // The bridge call sets `window.location`, so we cancel the SPA
          // navigation cleanly with `next(false)` and the browser takes over.
          const bridged = await tryPerformSsoRedirect({
            query: routeTo.query,
            axios: getCoreAxiosInstance(),
          });
          if (bridged) return next(false);
          return next({ name: 'home' });
        }
        next();
      },
    },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import(/* webpackChunkName: "auth" */ '@views/auth/register'),
    meta: {
      layout: 'auth',
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'home' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/confirm-account',
    name: 'confirm-account',
    component: () => import(/* webpackChunkName: "auth" */ '@views/auth/confirm'),
    meta: {
      layout: 'auth',
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'home' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/recover-password',
    name: 'recover-password',
    component: () => import(/* webpackChunkName: "auth" */ '@views/auth/recover-password'),
    meta: {
      layout: 'auth',
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'home' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/mobile/recover-password',
    name: 'mobile-recover-password',
    meta:{
      layout: 'auth'
    },
    component: () => import(/* webpackChunkName: "auth" */ '@views/auth/mobile-recover-password')
  },
  {
    path: '/shared-questionnaire',
    name: 'athletes-shared-questionnaire',
    meta:{
      layout: 'public',
    },
    component: () => import(/* webpackChunkName: "public" */ '@views/public/shared-questionnaire')
  },
  {
    path: '/shared-recipe/:recipeId',
    name: 'shared-recipe',
    meta:{
      layout: 'public',
    },
    component: () => import(/* webpackChunkName: "public" */ '@views/public/shared-recipe.vue')
  },
  {
    path: '/google-calendar-redirect',
    name: 'google-calendar-redirect',
    meta:{
      layout: 'public',
    },
    component: () => import(/* webpackChunkName: "public" */ '@views/public/google-calendar-redirect')
  },
  {
    path: '/coupon-usage',
    name: 'public-coupon-usage',
    meta:{
      layout: 'public',
    },
    component: () => import(/* webpackChunkName: "public" */ '@views/public/coupon-usage')
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import(/* webpackChunkName: "auth" */ '@views/auth/forgotPassword'),
    meta: {
      layout: 'auth',
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'home' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      layout: 'auth',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        store.dispatch('auth/logOut'); // logout user
        store.dispatch('food/reset'); // clear food state

        next({name: 'login'});
      },
    },
  },
  {
    path:'/admin',
    name: 'admin-home',
    component: () => import(/* webpackChunkName: "admin" */ '@views/admin/dashboard'),
    meta:{
      authRequired:true,
      adminRequired:true
    }
  },
  {
    path:'/admin/notifications',
    name: 'admin-notifications',
    component: () => import(/* webpackChunkName: "admin" */ '@views/admin/notifications'),
    meta:{
      authRequired:true,
      adminRequired:true
    }
  },
  {
    path: '/admin/coupons',
    name: 'admin-coupons',
    component: () => import(/* webpackChunkName: "admin" */ '@views/admin/Coupons'),
    meta:{
      authRequired: true,
      adminRequired: true
    }
  },
  {
    path:'/admin/users',
    name: 'admin-users',
    component: () => import(/* webpackChunkName: "admin" */ '@views/admin/userList'),
    meta:{
      authRequired:true,
      adminRequired:true
    }
  },
  {
    path:'/admin/users/:userId',
    name: 'admin-user-details',
    component: () => import(/* webpackChunkName: "admin" */ '@views/admin/userDetails'),
    meta:{
      authRequired:true,
      adminRequired:true
    }
  },
  {
    path:'/admin/transactions',
    name: 'admin-transactions',
    component: () => import(/* webpackChunkName: "admin" */ '@views/admin/transactionsList'),
    meta:{
      authRequired:true,
      adminRequired:true
    }
  },
  {
    path:'/admin/marketplace-profiles',
    name: 'admin-marketplace-profiles',
    component: () => import(/* webpackChunkName: "admin" */ '@views/admin/MarketplaceProfiles'),
    meta:{
      authRequired:true,
      adminRequired:true
    }
  },
  {
    path:'/admin/educational',
    name: 'admin-educational',
    component: () => import(/* webpackChunkName: "admin" */ '@views/admin/Educational'),
    meta:{
      authRequired:true,
      adminRequired:true
    }
  },
  {
    path: '/user',
    name: 'Profile',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/profile'),
    meta: {
      layout: 'account',
      authRequired: true,
    },
  },
  {
    path: '/user/subscription',
    name: 'user-subscription',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/userSubscription'),
    meta: {
      layout: 'account',
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/user/sms',
    name: 'sms',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/UserSmsManager'),
    meta: {
      layout: 'account',
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/user/subscription/:userTransactionId',
    name: 'pay-transaction',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/payTransaction'),
    meta:{
      layout: 'account',
      authRequired: true,
      parentRoute: 'user-subscription',
    }
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import(/* webpackChunkName: "general" */ '@views/general/terms'),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/training',
    name: 'training',
    component: () => import(/* webpackChunkName: "general" */ '@views/general/training'),
    meta: {
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/faq',
    name: 'faq',
    component: () => import(/* webpackChunkName: "general" */ '@views/general/faq'),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/calendar',
    name: 'calendar',
    component: () => import(/* webpackChunkName: "calendar" */ '@views/general/calendar'),
    meta: {
      authRequired: true,
      hideBreadcrumbs: true,
      hideFooter: true,
    },
  },
  {
    path: '/chat',
    name: 'chat',
    component: () => import(/* webpackChunkName: "chat" */ '@views/chat/Chat'),
    meta: {
      layout: 'chat',
      authRequired: true,
    },
  },
  {
    path: '/cloud',
    name: 'cloud',
    component: () => import(/* webpackChunkName: "cloud" */ '@views/cloud/CloudHome'),
    meta: {
      layout:'cloud',
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/cloud/users/:userId',
    name: 'cloud-user',
    component: () => import(/* webpackChunkName: "cloud" */ '@views/cloud/CloudUser'),
    meta: {
      layout:'cloud',
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/cloud/users/:userId/listings',
    name: 'cloud-user-listings',
    component: () => import(/* webpackChunkName: "cloud" */ '@views/cloud/CloudUserListings'),
    meta: {
      layout:'cloud',
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/cloud/listings/:listingId',
    name: 'cloud-listing',
    component: () => import(/* webpackChunkName: "cloud" */ '@views/cloud/CloudListing'),
    meta: {
      layout:'cloud',
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/cloud/diet-plans',
    name: 'cloud-diet-plans',
    component: () => import(/* webpackChunkName: "cloud" */ '@views/cloud/CloudDietPlans'),
    meta: {
      layout:'cloud',
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/cloud/meals',
    name: 'cloud-meals',
    component: () => import(/* webpackChunkName: "cloud" */ '@views/cloud/CloudMealTemplates'),
    meta: {
      layout:'cloud',
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/cloud/foods',
    name: 'cloud-foods',
    component: () => import(/* webpackChunkName: "cloud" */ '@views/cloud/CloudFoods'),
    meta: {
      layout:'cloud',
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/cloud/recipes',
    name: 'cloud-recipes',
    component: () => import(/* webpackChunkName: "cloud" */ '@views/cloud/CloudRecipes'),
    meta: {
      layout:'cloud',
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/athletes',
    name: 'athletes',
    component: () => import(/* webpackChunkName: "athletes", webpackPrefetch: true */ '@views/athletes/athleteList'),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/athletes/statistics',
    name: 'athletes-stats',
    component: () => import(/* webpackChunkName: "athletes" */ '@views/athletes/athleteStatistics'),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/athletes/add',
    name: 'add-athlete',
    component: () => import(/* webpackChunkName: "athletes" */ '@views/athletes/addAthlete'),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/athletes/:athleteId',
    name: 'view-athlete',
    component: () => import(/* webpackChunkName: "athletes" */ '@views/athletes/athlete/athleteBase'),
    meta:{
      authRequired: true
    }
  },
  {
    path: '/athletes/:athleteId/diet-plan/:dietPlanId',
    name: 'athlete-diet-plan',
    component: () => import(/* webpackChunkName: "diet-editor" */ '@views/athletes/athlete/dietPlans/athleteDietPlanView'),
    meta: {
      authRequired: true
    }
  },
  {
    path: '/user/change-password',
    name: 'change-password',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/changePasswordForm'),
    meta: {
      layout: 'account',
      authRequired: true,
    },
  },
  {
    path: '/user/passcode',
    name: 'passcode-settings',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/passcodeSettings'),
    meta: {
      layout: 'account',
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/user/passcode-removal',
    name: 'passcode-removal',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/passcodeRemoval'),
    meta: {
      layout: 'account',
      authRequired: true,
      blockStudents: true,
      parentRoute: 'passcode-settings',
    },
  },
  {
    path: '/user/todo-list',
    name: 'user-todo',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/toDoListForm'),
    meta: {
      layout: 'account',
      authRequired: true,
    },
  },
  {
    path: '/user/company',
    name: 'company',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/userCompanyDetailsForm'),
    meta: {
      layout: 'account',
      authRequired: true,
    },
  },
  {
    path: '/user/tags',
    name: 'user-tags',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/userTags'),
    meta: {
      layout: 'account',
      authRequired: true,
    },
  },
  {
    path: '/user/connected-agents',
    name: 'connected-agents',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/connectedAgents'),
    meta: {
      layout: 'account',
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/oauth/consent',
    name: 'oauth-consent',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/oauthConsent'),
    meta: {
      layout: 'auth',
      authRequired: true,
      blockStudents: true,
    },
  },
  {
    path: '/user/print-templates',
    name: 'print-templates',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/PrintTemplates'),
    meta: {
      layout: 'account',
      authRequired: true,
    },
  },
  {
    path: '/user/print-templates/:templateId',
    name: 'edit-print-template',
    component: () => import(/* webpackChunkName: "print-editor" */ '@views/user/PrintTemplatesEditor'),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/user/note-templates',
    name: 'note-templates',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/noteTemplates/NoteTemplates'),
    meta: {
      layout: 'account',
      authRequired: true,
    },
  },
  {
    path: '/user/note-templates/:noteId',
    name: 'edit-note-template',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/noteTemplates/EditNote'),
    meta: {
      layout: 'account',
      authRequired: true,
      parentRoute: 'note-templates',
    },
  },
  {
    path: '/user/questionnaires',
    name: 'questionnaires',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/questionnaires/QuestionnaireList'),
    meta: {
      layout: 'account',
      authRequired: true,
    },
  },
  {
    path: '/user/questionnaires/:questionnaireId',
    name: 'edit-questionnaire',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/questionnaires/EditQuestionnaire'),
    meta: {
      layout: 'account',
      authRequired: true,
      parentRoute: 'questionnaires',
    },
  },
  {
    path: '/user/purchase-athlisis',
    name: 'purchase-athlisis',
    component: () => import(/* webpackChunkName: "account" */ '@views/user/purchaseAthlisis'),
    meta:{
      layout: 'auth',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.state.auth.currentUser.isPremium) {
          // Redirect to the home page instead
          next({ name: 'home' })
        } else {
          // Continue to the login page
          next()
        }
      },
    }
  },
  {
    path: '/auth/paused',
    name: 'auth-paused',
    component: () => import(/* webpackChunkName: "auth" */ '@views/auth/paused'),
    meta:{
      layout: 'auth',
      authRequired: true,
    }
  },
  {
    path: '/notifications',
    name: 'user-notifications',
    component: () => import(/* webpackChunkName: "general" */ '@views/notifications/notificationList'),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/foods',
    name: 'foods',
    component: () => import(/* webpackChunkName: "food-recipes", webpackPrefetch: true */ '@views/food/foodList'),
    meta: {
      authRequired: true
    }
  },
  {
    path: '/diet-plans',
    name: 'diet-plans',
    component: () => import(/* webpackChunkName: "diet-plans", webpackPrefetch: true */ '@views/dietPlan/list/dietPlanListView'),
    meta: {
      authRequired: true
    }
  },
  {
    path: '/meals',
    name: 'meals',
    component: () => import(/* webpackChunkName: "diet-plans" */ '@views/dietPlan/list/mealTemplateView'),
    meta: {
      authRequired: true
    }
  },
  {
    path: '/recipes',
    name: 'recipes',
    component: () => import(/* webpackChunkName: "food-recipes" */ '@views/recipes/RecipeList'),
    meta: {
      authRequired: true
    }
  },
  {
    path: '/recipes/:recipeId',
    name: 'recipe',
    component: () => import(/* webpackChunkName: "food-recipes" */ '@views/recipes/Recipe'),
    meta: {
      authRequired: true
    }
  },
  {
    path: '/diet-plans/statistics',
    name: 'diet-plan-statistics',
    component: () => import(/* webpackChunkName: "diet-plans" */ '@views/dietPlan/dietPlanStatistics'),
    meta: {
      authRequired: true
    }
  },
  {
    path: '/diet-plans/notes',
    redirect: { name: 'note-templates' },
  },
  {
    path: '/diet-plans/notes/:noteId',
    redirect: to => ({ name: 'edit-note-template', params: { noteId: to.params.noteId } }),
  },
  {
    path: '/diet-plans/:dietPlanId',
    name: 'diet-plan',
    component: () => import(/* webpackChunkName: "diet-editor" */ '@views/dietPlan/details/dietPlanDetailsView'),
    meta: {
        authRequired: true
      }
  },
  {
    path: '/500',
    name: '500',
    meta:{
      layout: 'auth',
    },
    component: Timeout,
  },
  {
    path: '/404',
    name: '404',
    meta:{
      layout: 'auth',
    },
    component: NotFound,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },

  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
]
